import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheck,
  faUndo,
  faInfoCircle,
  faPaintBrush
} from '@fortawesome/free-solid-svg-icons';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatNumber = (value, decimals = 2) => {
  const num = toNumber(value);
  if (num === null) return '-';
  return num.toLocaleString('en-CA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const PanelBayPaintingModal = ({
  isOpen,
  span,
  onClose,
  onSave
}) => {
  const [panelBays, setPanelBays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const spanTotalArea = toNumber(span?.total_area) || 0;
  const spanDamageArea = toNumber(span?.inspection_damage_area) || 0;
  // Support both new and old field names
  const childElements = span?.child_elements || span?.panel_bays || [];
  const defaultPanelCount = childElements.length || 0;
  const defaultAverageArea = defaultPanelCount > 0 ? spanTotalArea / defaultPanelCount : spanTotalArea;
  const defaultDamagePerBay = defaultPanelCount > 0 ? spanDamageArea / defaultPanelCount : 0;

  const initializeState = () => {
    if (!childElements.length) {
      return [];
    }

    return childElements.map((bay, index) => {
      const averageArea = toNumber(bay.averageArea) || defaultAverageArea || 0;
      const estimatedDamage =
        toNumber(bay.estimatedDamageArea) ??
        toNumber(bay.inspectionSummary?.latestDamageArea) ??
        defaultDamagePerBay ??
        0;

      const existingPaintPercentage = toNumber(bay.paintPercentage);
      const derivedPercentage = averageArea > 0
        ? clamp(Math.round(((estimatedDamage || 0) / averageArea) * 100), 0, 100)
        : 0;

      const paintPercentage = existingPaintPercentage !== null ? existingPaintPercentage : derivedPercentage;
      const paintedArea = bay.paintedArea !== null && bay.paintedArea !== undefined
        ? toNumber(bay.paintedArea) || 0
        : parseFloat(((averageArea * paintPercentage) / 100).toFixed(2));

      const markUndamaged = bay.paintStatus === 'undamaged' || (estimatedDamage === 0 && paintPercentage === 0);

      return {
        id: bay.id,
        identifier: bay.childElementIdentifier || bay.panelBayIdentifier || `Child ${index + 1}`,
        averageArea,
        estimatedDamageArea: estimatedDamage,
        paintPercentage,
        paintedArea,
        paintStatus: bay.paintStatus || 'pending',
        markUndamaged
      };
    });
  };

  useEffect(() => {
    if (isOpen && span) {
      setPanelBays(initializeState());
      setSaving(false);
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, span?.id]);

  const totals = useMemo(() => {
    return panelBays.reduce(
      (acc, bay) => {
        acc.totalPaintedArea += bay.paintedArea || 0;
        acc.totalEstimatedDamage += bay.estimatedDamageArea || 0;
        acc.remainingDamage += Math.max(
          0,
          (bay.estimatedDamageArea ?? bay.averageArea ?? 0) - (bay.paintedArea || 0)
        );
        return acc;
      },
      { totalPaintedArea: 0, totalEstimatedDamage: 0, remainingDamage: 0 }
    );
  }, [panelBays]);

  const adjustPaintPercentage = (index, delta) => {
    setPanelBays(prev => {
      const updated = [...prev];
      const target = { ...updated[index] };
      let nextPercentage = clamp((target.paintPercentage || 0) + delta, 0, 100);
      if (target.markUndamaged) {
        nextPercentage = 0;
      }
      const paintedArea = target.averageArea
        ? parseFloat(((target.averageArea * nextPercentage) / 100).toFixed(2))
        : 0;

      updated[index] = {
        ...target,
        paintPercentage: nextPercentage,
        paintedArea,
        markUndamaged: nextPercentage === 0 ? target.markUndamaged : false,
        paintStatus: nextPercentage === 0 ? 'pending' : nextPercentage >= 100 ? 'painted' : 'partial'
      };
      return updated;
    });
  };

  const handleUndamagedToggle = (index) => {
    setPanelBays(prev => {
      const updated = [...prev];
      const target = { ...updated[index] };
      const nextValue = !target.markUndamaged;

      updated[index] = {
        ...target,
        markUndamaged: nextValue,
        paintPercentage: nextValue ? 0 : target.paintPercentage,
        paintedArea: nextValue ? 0 : target.paintedArea,
        paintStatus: nextValue ? 'undamaged' : (target.paintPercentage > 0 ? target.paintStatus : 'pending')
      };
      return updated;
    });
  };

  const handlePercentageChange = (index, value) => {
    const nextValue = clamp(Number(value) || 0, 0, 100);
    setPanelBays(prev => {
      const updated = [...prev];
      const target = { ...updated[index] };
      const paintedArea = target.averageArea
        ? parseFloat(((target.averageArea * nextValue) / 100).toFixed(2))
        : 0;

      updated[index] = {
        ...target,
        paintPercentage: nextValue,
        paintedArea,
        markUndamaged: false,
        paintStatus: nextValue === 0 ? 'pending' : nextValue >= 100 ? 'painted' : 'partial'
      };
      return updated;
    });
  };

  const handlePaintedAreaChange = (index, value) => {
    let nextValue = Number(value) || 0;
    setPanelBays(prev => {
      const updated = [...prev];
      const target = { ...updated[index] };
      nextValue = clamp(nextValue, 0, target.averageArea || 0);
      const paintPercentage = target.averageArea
        ? parseFloat(((nextValue / target.averageArea) * 100).toFixed(2))
        : 0;

      updated[index] = {
        ...target,
        paintedArea: nextValue,
        paintPercentage,
        markUndamaged: false,
        paintStatus: paintPercentage === 0 ? 'pending' : paintPercentage >= 100 ? 'painted' : 'partial'
      };
      return updated;
    });
  };

  const resetPanelBay = (index) => {
    setPanelBays(prev => {
      const updated = [...prev];
      updated[index] = initializeState()[index];
      return updated;
    });
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = panelBays.map(bay => {
        const estimatedDamage = bay.estimatedDamageArea ?? bay.averageArea ?? 0;
        const remainingDamage = Math.max(0, parseFloat((estimatedDamage - (bay.paintedArea || 0)).toFixed(2)));
        return {
          id: bay.id,
          paintPercentage: bay.paintPercentage,
          paintedArea: bay.paintedArea,
          markUndamaged: bay.markUndamaged,
          remainingDamageArea: remainingDamage
        };
      });

      await onSave(payload);
    } catch (err) {
      setError(err?.message || 'Failed to save panel bay updates');
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
              <FontAwesomeIcon icon={faPaintBrush} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Panel Bay Painting</h2>
              <p className="text-sm text-gray-600">
                Record painted percentages for <span className="font-medium">{span?.span_code}</span> — {panelBays.length} panel bay{panelBays.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4">
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800 flex items-start gap-3">
            <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5" />
            <div>
              <p className="font-semibold">How this works</p>
              <p className="mt-1">
                Start from the inspected damage percentage (default). Adjust in 5% or 10% increments to capture the actual painted coverage.
                Use “Mark undamaged” when no work was needed. Painted area must stay within the panel bay size.
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          {panelBays.length === 0 ? (
            <div className="py-10 px-6 border border-dashed border-gray-300 rounded-lg text-center text-sm text-gray-500">
              Panel bay data is not available for this span yet. Configure panel bays for the span to enable painting records.
            </div>
          ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Panel Bay</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Damage &amp; Area</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coverage &amp; Painted Area</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {panelBays.map((bay, index) => {
                  const damagePercentage = bay.averageArea > 0 ? (bay.estimatedDamageArea || 0) / bay.averageArea * 100 : 0;
                  const warning = !bay.markUndamaged && bay.estimatedDamageArea && bay.paintPercentage < damagePercentage;
                  return (
                    <tr key={bay.id} className={bay.markUndamaged ? 'bg-green-50/70' : undefined}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        <div className="space-y-1">
                          <div>{bay.identifier}</div>
                          {warning && (
                            <div className="text-xs text-red-500">
                              Painting below inspected damage ({formatNumber(damagePercentage, 1)}%)
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="space-y-1">
                          <div>
                            <span className="text-xs uppercase text-gray-500">Damage • </span>
                            {formatNumber(bay.estimatedDamageArea)} m²
                          </div>
                          <div>
                            <span className="text-xs uppercase text-gray-500">Average • </span>
                            {formatNumber(bay.averageArea)} m²
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="space-y-2">
                          <div className="flex items-center flex-wrap gap-2">
                            <div className="flex items-center overflow-hidden rounded-md border border-gray-200 shadow-sm">
                              <button
                                type="button"
                                onClick={() => adjustPaintPercentage(index, -10)}
                                className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                                disabled={bay.markUndamaged}
                              >
                                -10
                              </button>
                              <button
                                type="button"
                                onClick={() => adjustPaintPercentage(index, -5)}
                                className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 border-l border-gray-200"
                                disabled={bay.markUndamaged}
                              >
                                -5
                              </button>
                            </div>
                            <div className="relative">
                              <input
                                type="number"
                                className="w-20 px-3 py-1 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full text-center pr-6 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                value={bay.paintPercentage ?? 0}
                                min="0"
                                max="100"
                                step="1"
                                onChange={(e) => handlePercentageChange(index, e.target.value)}
                                disabled={bay.markUndamaged}
                              />
                              <span className="absolute inset-y-0 right-3 flex items-center text-xs text-blue-600 font-semibold">%</span>
                            </div>
                            <div className="flex items-center overflow-hidden rounded-md border border-gray-200 shadow-sm">
                              <button
                                type="button"
                                onClick={() => adjustPaintPercentage(index, 5)}
                                className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 border-r border-gray-200"
                                disabled={bay.markUndamaged}
                              >
                                +5
                              </button>
                              <button
                                type="button"
                                onClick={() => adjustPaintPercentage(index, 10)}
                                className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                                disabled={bay.markUndamaged}
                              >
                                +10
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                className="w-24 px-3 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                                value={bay.paintedArea ?? 0}
                                min="0"
                                max={bay.averageArea || 0}
                                step="0.25"
                                onChange={(e) => handlePaintedAreaChange(index, e.target.value)}
                                disabled={bay.markUndamaged}
                              />
                              <span className="text-xs text-gray-500">m²</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              Remaining {formatNumber(Math.max(0, (bay.estimatedDamageArea ?? 0) - (bay.paintedArea || 0)))} m²
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="flex items-center gap-3 flex-wrap">
                          <button
                            type="button"
                            onClick={() => handleUndamagedToggle(index)}
                            className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
                              bay.markUndamaged
                                ? 'bg-green-100 text-green-700 border-green-300'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                            }`}
                          >
                            <FontAwesomeIcon icon={faCheck} className="mr-1" />
                            {bay.markUndamaged ? 'Undamaged' : 'Mark undamaged'}
                          </button>
                          <button
                            type="button"
                            onClick={() => resetPanelBay(index)}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                          >
                            <FontAwesomeIcon icon={faUndo} />
                            Reset
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}

          <div className={`mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 ${panelBays.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Painted Area</p>
              <p className="mt-1 text-2xl font-bold text-blue-600">
                {formatNumber(totals.totalPaintedArea)} m²
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Across {panelBays.length} panel bay{panelBays.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Estimated Damage</p>
              <p className="mt-1 text-2xl font-bold text-orange-600">
                {formatNumber(totals.totalEstimatedDamage)} m²
              </p>
              <p className="text-xs text-gray-500 mt-1">Based on inspection data or evenly distributed estimate</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Remaining Damage</p>
              <p className={`mt-1 text-2xl font-bold ${totals.remainingDamage <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatNumber(totals.remainingDamage)} m²
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Calculated as inspected damage minus painted area
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-white transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={saving || panelBays.length === 0}
          >
            <FontAwesomeIcon icon={faCheck} />
            {saving ? 'Saving...' : 'Save Updates'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PanelBayPaintingModal;

